import { useAuth } from '@/modules/auth/auth';
import { type ApiResponse } from '@phottr-org/shared/src/types/api-response';

export interface ApiRequestOptions {
  noAuth?: boolean;
}

export function useApi() {
  const { getAccessToken, impersonateUserId } = useAuth();

  async function get<RES>(path: string, options?: ApiRequestOptions) {
    let accessToken;
    if (!options?.noAuth) {
      accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('Failed to get access token');
      }
    }
    const res = await fetch(`/api${path}`, {
      headers: {
        'X-Auth-Token': accessToken ?? '',
        'X-Impersonate': impersonateUserId.value,
        'Content-Type': 'application/json',
      },
    });

    return res.json() as Promise<RES>;
  }

  async function post<REQ, RES>(
    path: string,
    body: REQ,
    options?: ApiRequestOptions,
  ): Promise<ApiResponse<RES>> {
    let accessToken;
    if (!options?.noAuth) {
      accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('Failed to get access token');
      }
    }

    let res;
    try {
      res = await fetch(`/api${path}`, {
        method: 'POST',
        headers: {
          'X-Auth-Token': accessToken ?? '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
    } catch (err) {
      console.error(err);
      return {
        error: {
          code: 'fetch-failed',
          message: 'Failed to connect to the cloud, please check your internet connection',
        },
      };
    }

    if (!res.ok) {
      console.error(`Unexpected response from the cloud`);
      return {
        error: {
          code: 'fetch-not-ok',
          message: 'Unexpected response from the cloud, please try again later or contact support',
        },
      };
    }

    let json;
    try {
      json = await res.json();
    } catch (err) {
      console.error(err);
      return {
        error: {
          code: 'fetch-json-failed',
          message: 'Unexpected response from the cloud, please try again later or contact support',
        },
      };
    }

    if (json.error) {
      return { error: json.error };
    } else {
      return { data: json };
    }
  }

  async function put<REQ, RES>(
    path: string,
    body: REQ,
    options?: ApiRequestOptions,
  ): Promise<ApiResponse<RES>> {
    let accessToken;
    if (!options?.noAuth) {
      accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('Failed to get access token');
      }
    }

    let res;
    try {
      res = await fetch(`/api${path}`, {
        method: 'PUT',
        headers: {
          'X-Auth-Token': accessToken ?? '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
    } catch (err) {
      console.error(err);
      return {
        error: {
          code: 'fetch-failed',
          message: 'Failed to connect to the cloud, please check your internet connection',
        },
      };
    }

    if (!res.ok) {
      console.error(`Unexpected response from the cloud`);
      return {
        error: {
          code: 'fetch-not-ok',
          message: 'Unexpected response from the cloud, please try again later or contact support',
        },
      };
    }

    let json;
    try {
      json = await res.json();
    } catch (err) {
      console.error(err);
      return {
        error: {
          code: 'fetch-json-failed',
          message: 'Unexpected response from the cloud, please try again later or contact support',
        },
      };
    }

    if (json.error) {
      return { error: json.error };
    } else {
      return { data: json };
    }
  }

  // TODO: move to axios or something
  async function putBuffer<RES>(
    path: string,
    contentType: string,
    body: ArrayBuffer,
    options?: ApiRequestOptions,
  ): Promise<ApiResponse<RES>> {
    let accessToken;
    if (!options?.noAuth) {
      accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('Failed to get access token');
      }
    }

    let res;
    try {
      res = await fetch(`/api${path}`, {
        method: 'PUT',
        headers: {
          'X-Auth-Token': accessToken ?? '',
          'Content-Type': contentType,
        },
        body,
      });
    } catch (err) {
      console.error(err);
      return {
        error: {
          code: 'fetch-failed',
          message: 'Failed to connect to the cloud, please check your internet connection',
        },
      };
    }

    if (!res.ok) {
      console.error(`Unexpected response from the cloud`);
      return {
        error: {
          code: 'fetch-not-ok',
          message: 'Unexpected response from the cloud, please try again later or contact support',
        },
      };
    }

    let json;
    try {
      json = await res.json();
    } catch (err) {
      console.error(err);
      return {
        error: {
          code: 'fetch-json-failed',
          message: 'Unexpected response from the cloud, please try again later or contact support',
        },
      };
    }

    if (json.error) {
      return { error: json.error };
    } else {
      return { data: json };
    }
  }

  return { get, post, put, putBuffer };
}
